import React, { useId } from 'react';

/**
 * Vlaječky k jazykům v přepínači.
 *
 * Kreslí se ručně jako SVG, ne emoji: Windows nemá pro vlajková emoji font,
 * takže by se místo české vlajky ukázalo „CZ". A ne obrázky ze sítě: Bloom
 * nemá stahovat nic, co by o čtenáři někomu jinému řeklo, že je tady.
 *
 * Vlajka není jazyk — to je na tom to nepohodlné. Angličtinu mluví víc lidí
 * mimo Británii než v ní, španělsky se mluví hlavně v Latinské Americe.
 * Volíme tam, kde překlad vznikl: britská angličtina (píšeme „organisation"),
 * brazilská portugalština (tak je psaná), zjednodušená čínština (tj. ČLR).
 * Vlaječka je vodítko pro oko, ne tvrzení o tom, komu jazyk patří — proto je
 * vedle ní vždycky i název jazyka, napsaný tím jazykem.
 *
 * Kreslí se do 24×16, což je poměr 3:2. Skutečné vlajky mají poměry různé,
 * ale v řadě pod sebou vypadá stejný rám líp než přesnost na papíře.
 */

// Pěticípá hvězda se středem v nule a poloměrem 1, špičkou nahoru.
const HVEZDA = 'M0,-1L.225,-.309L.951,-.309L.363,.118L.588,.809'
             + 'L0,.382L-.588,.809L-.363,.118L-.951,-.309L-.225,-.309Z';

/** Hvězda o daném středu a poloměru. */
function Hvezda({ x, y, r, barva }) {
  return <path d={HVEZDA} fill={barva} transform={`translate(${x} ${y}) scale(${r})`} />;
}

/** Dva pruhy na výšku pod sebou — nejčastější vzor ze všech. */
function Pulena({ horni, dolni }) {
  return (
    <>
      <rect width="24" height="8" fill={horni} />
      <rect y="8" width="24" height="8" fill={dolni} />
    </>
  );
}

/** Tři pruhy na šířku. */
function Trikolora({ barvy }) {
  return barvy.map((barva, i) => (
    <rect key={barva} x={i * 8} width="8" height="16" fill={barva} />
  ));
}

/** Tři pruhy na výšku. */
function Trojpruh({ barvy }) {
  return barvy.map((barva, i) => (
    <rect key={barva} y={i * (16 / 3)} width="24" height={16 / 3} fill={barva} />
  ));
}

function Cesko() {
  return (
    <>
      <Pulena horni="#fff" dolni="#d7141a" />
      <path d="M0 0l12 8L0 16z" fill="#11457e" />
    </>
  );
}

function Britanie({ id }) {
  return (
    <>
      <rect width="24" height="16" fill="#012169" />
      {/* Šikmé pruhy se ořežou rámem vlajky, ať nepřečuhují do rohů. */}
      <clipPath id={`${id}-ram`}><rect width="24" height="16" /></clipPath>
      <g clipPath={`url(#${id}-ram)`}>
        <path d="M0 0L24 16M24 0L0 16" stroke="#fff" strokeWidth="3.2" />
        <path d="M0 0L24 16M24 0L0 16" stroke="#c8102e" strokeWidth="1.8" />
      </g>
      <path d="M12 0v16M0 8h24" stroke="#fff" strokeWidth="5.3" />
      <path d="M12 0v16M0 8h24" stroke="#c8102e" strokeWidth="3.2" />
    </>
  );
}

function Spanelsko() {
  return (
    <>
      <rect width="24" height="16" fill="#c60b1e" />
      <rect y="4" width="24" height="8" fill="#ffc400" />
    </>
  );
}

function Brazilie({ id }) {
  return (
    <>
      <rect width="24" height="16" fill="#009b3a" />
      <path d="M12 1.8l10.2 6.2L12 14.2 1.8 8z" fill="#fedf00" />
      <circle cx="12" cy="8" r="3.7" fill="#002776" />
      <clipPath id={`${id}-kruh`}><circle cx="12" cy="8" r="3.7" /></clipPath>
      <path d="M7.4 9.9a6.6 6.6 0 0 1 9.6-1.4" fill="none" stroke="#fff"
            strokeWidth="1.1" clipPath={`url(#${id}-kruh)`} />
    </>
  );
}

function Vietnam() {
  return (
    <>
      <rect width="24" height="16" fill="#da251d" />
      <Hvezda x={12} y={8} r={4.4} barva="#ffff00" />
    </>
  );
}

function Thajsko() {
  return (
    <>
      <rect width="24" height="16" fill="#a51931" />
      <rect y="2.67" width="24" height="10.66" fill="#f4f5f8" />
      <rect y="5.33" width="24" height="5.34" fill="#2d2a4a" />
    </>
  );
}

function Filipiny() {
  return (
    <>
      <Pulena horni="#0038a8" dolni="#ce1126" />
      <path d="M0 0l11 8L0 16z" fill="#fff" />
      <circle cx="3.3" cy="8" r="1.6" fill="#fcd116" />
      {/* Tři hvězdy v rozích trojúhelníku; v téhle velikosti jako tečky. */}
      <circle cx="1.3" cy="2.2" r=".7" fill="#fcd116" />
      <circle cx="1.3" cy="13.8" r=".7" fill="#fcd116" />
      <circle cx="8.4" cy="8" r=".7" fill="#fcd116" />
    </>
  );
}

function Cina() {
  return (
    <>
      <rect width="24" height="16" fill="#de2910" />
      <Hvezda x={4} y={4} r={2.6} barva="#ffde00" />
      <Hvezda x={8.2} y={1.7} r={.9} barva="#ffde00" />
      <Hvezda x={9.9} y={3.4} r={.9} barva="#ffde00" />
      <Hvezda x={9.9} y={5.7} r={.9} barva="#ffde00" />
      <Hvezda x={8.2} y={7.4} r={.9} barva="#ffde00" />
    </>
  );
}

function Japonsko() {
  return (
    <>
      <rect width="24" height="16" fill="#fff" />
      <circle cx="12" cy="8" r="4.8" fill="#bc002d" />
    </>
  );
}

const VLAJKY = {
  cs: Cesko,
  en: Britanie,
  es: Spanelsko,
  fr: () => <Trikolora barvy={['#002395', '#ffffff', '#ed2939']} />,
  pt: Brazilie,
  de: () => <Trojpruh barvy={['#000000', '#dd0000', '#ffce00']} />,
  pl: () => <Pulena horni="#fff" dolni="#dc143c" />,
  uk: () => <Pulena horni="#005bbb" dolni="#ffd500" />,
  vi: Vietnam,
  th: Thajsko,
  id: () => <Pulena horni="#ce1126" dolni="#fff" />,
  tl: Filipiny,
  zh: Cina,
  ja: Japonsko,
};

/**
 * Vlaječka jazyka. Neznámý kód nekreslí nic — přepínač zůstane čitelný
 * i pro jazyk, ke kterému jsme vlajku ještě nenakreslili.
 */
export default function Vlajka({ kod, className = '' }) {
  // `useId` proto, že v rozbaleném seznamu je vlajka dvakrát (v seznamu
  // i na tlačítku) a dvě stejná `id` v jednom dokumentu jsou chyba.
  const id = useId().replace(/:/g, '');
  const Kresba = VLAJKY[kod];
  if (!Kresba) return null;

  return (
    <svg viewBox="0 0 24 16" className={`w-[18px] h-3 shrink-0 rounded-[1px] ${className}`}
         aria-hidden="true" focusable="false">
      <Kresba id={`vlajka-${id}`} />
      {/* Bílé vlajky by se na světlém pozadí ztratily. */}
      <rect width="24" height="16" fill="none" stroke="rgba(0,0,0,.2)" strokeWidth="1.4" />
    </svg>
  );
}
